import { t } from "@lingui/macro";
import { useEffect, useState } from "react";
import { useHistory, useParams } from '../../hooks/useRouterCompat';
import { useAppUIContext } from "../../context/AppUIContext";
import { Soribot } from "../../models/Soribot";
import { My } from "../../services/My";
import ProfileImg from "../components/ProfileImg";
import { SoribotChatContent } from "../rooms/right-menu/BotSoriPrivateModal";
import SoribotDatasources from "./SoribotDatasources";
import SoribotDetails from "./SoribotDetails";
import SoribotExposedAPI from "./SoribotExposedAPI";
import SoribotKnowledgeViewer from "./SoribotKnowledgeViewer";
import SoribotPublic from "./SoribotPublic";
import SoribotRooms from "./SoribotRooms";
import SoribotSuggestions from "./SoribotSuggestions";
import SoribotTestCases from "./SoribotTestCases";
import SoribotTestRuns from "./SoribotTestRuns";
import SoribotStats from "./SoribotStats";
import { useToast } from "../../components/ui/use-toast";
import { Button } from "../../components/ui/button";
import { ArrowLeft } from "lucide-react";

export type SoribotSettingPage = 'details' | 'datasources' | 'knowledgeviewer' | 'freechat' | 'testcases' | 'testruns' | 'suggestions' | 'rooms' | 'public' | 'api' | 'messages';

export const SoribotSettingPage: React.FC<{ page: SoribotSettingPage }> = ({ page }) => {
  const { soribots } = useAppUIContext();
  const { botId } = useParams<{ botId: string; }>();
  const [bot, setBot] = useState<Soribot | null>();
  const { toast } = useToast();
  const history = useHistory();

  useEffect(() => {
    const b = soribots.find(b => b.id === botId);
    setBot(b);
    if (b && !b.userIds.includes(My.user.id)) {
      toast({
        variant: "destructive",
        title: t`Access Denied`,
        description: t`Bot not found or you may not have access to it.`
      });
      history.replace("/");
    }
  }, [botId, soribots])

  return (
    <div className="flex flex-col h-screen bg-slate-50">
      <header className="flex items-center justify-between px-4 h-14 border-b bg-white shrink-0">
        <div className="flex items-center gap-2 overflow-hidden">
          <Button variant="ghost" size="icon" onClick={() => history.goBack()}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold truncate">
            {bot?.name} <span className="text-muted-foreground font-normal">- {getHeader()}</span>
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full overflow-hidden border">
            <ProfileImg url={My.user.photoUrl} className={'w-full h-full object-cover'} />
          </div>
        </div>
      </header>

      {bot && (
        <div className="flex-1 overflow-hidden flex flex-col relative">
          {page == 'freechat' ? (
            <SoribotChatContent bot={bot} room={null} />
          ) : (
            <div className="flex-1 overflow-y-auto p-4 max-w-5xl mx-auto w-full">
              {page == 'details' && <SoribotDetails bot={bot} />}
              {page == 'datasources' && <SoribotDatasources bot={bot} />}
              {page == 'knowledgeviewer' && <SoribotKnowledgeViewer bot={bot} />}
              {page == 'testcases' && <SoribotTestCases bot={bot} />}
              {page == 'testruns' && <SoribotTestRuns bot={bot} />}
              {page == 'suggestions' && <SoribotSuggestions bot={bot} />}
              {page == 'rooms' && <SoribotRooms bot={bot} />}
              {page == 'public' && <SoribotPublic bot={bot} />}
              {page == 'api' && <SoribotExposedAPI bot={bot} />}
              {page == 'messages' && <SoribotStats bot={bot} />}
            </div>
          )}
        </div>
      )}
    </div>
  );

  function getHeader(): string {
    switch (page) {
      case 'details': return t`Details`;
      case 'datasources': return t`Data sources`;
      case 'knowledgeviewer': return t`Knowledge viewer`;
      case 'freechat': return t`Free chat`;
      case 'testcases': return t`Test cases`;
      case 'testruns': return t`Test runs`;
      case 'suggestions': return t`Suggestions`;
      case 'rooms': return t`Rooms`;
      case 'public': return t`Public page`;
      case 'api': return t`Exposed API`;
      case 'messages': return t`Messages`;
    }
  }
}

export default SoribotSettingPage;